import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import formatToUTCWithGMT from "../../utils/dateHelper";
import ManageProgressBar from "../manageProgressBar";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import MGAAPI from "../../api";
import displayError from "../../constants/utils";
import { progressAtom } from "../../utils/atoms/progressAtom";
import CircularTimer from "../CircularProgress";
import { AccordionItem } from "../AccordianItem";
import {
  Button,
  Grid,
  LoadingOverlay,
  Text,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import "./simulation.scss";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import OrderTrackingVertical from "../result/simulationProgress";
import SimulationTable from "./simulationTable";
import ScenariosTable from "./scenariosTable";
import _ from "lodash";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import KPILimitsRange from "../common/KPILimitsRange";
import { SeIcon } from "@se/web-ui-react";
import { defaultKpiFactors, useAssessmentRouteParams, useCurrentDesignComponentLists, useCurrentDesignVersion } from "../../constants/helper";
import { SelectedKpiSkeleton } from "../Skeleton";
import { getErrorMessage } from "../../utils/errorUtils";
import { IconPercentage } from "@tabler/icons-react";

const MGA_API = new MGAAPI();

const Simulation = () => {
  const { t } = useTranslation();
  const [userScope] = useAtom(userScopeAtom);

  const [loading, setLoading] = useState(false);
  const [progressListAtom] = useAtom(progressAtom);
  const { colorScheme } = useMantineColorScheme();
  // const { projectId } = useParams();
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "success",
    canClose: true,
    message: t("simulation.snackbar.success"),
    icon: "information_circle",
    button: t("common.close"),
    duration: 5000,
  });

  const [projectDetails, setProjectDetails] = useAtom(projectDetailsAtom);
  const [showSimulation, setShowSimulation] = useState(false);
  const [showError, setShowError] = useState(false);
  const [simulationType, setSimulationType] = useState("SINGLE");
  const [showSimuationResult, setShowSimulationResult] = useState({});
  const [initialSimualtionTable, setInitialSimulationTable] = useState([]);
  const [apiSimulationTable, setApiSimulationTable] = useState([]);
  const [initialSensitivityData, setInitialSensitivityData] = useState([]);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [sensitivityDirty, setSensitivityDirty] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isScenarioSelectionValid, setIsScenarioSelectionValid] = useState(true);
  const [cartesianSummary, setCartesianSummary] = useState({ expression: "0", total: 0, remaining: 100 });

  const currentYearMinusOne = Number(moment().subtract(1, 'year').format('YYYY'));
  const INITIAL_SIMULATION_SETTINGS = {
    startYear: (currentYearMinusOne),
    lifetime: 25,
    inflationRate: 2,
    discountRate: 8,
  };

  const [simulationSettings, setSimulationSettings] = useState(INITIAL_SIMULATION_SETTINGS);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [simulationErrors, setSimulationErrors] = useState({
    lifetime: null,
    inflationRate: null,
    discountRate: null,
  });

  // Define ranges for the fields; adjust as needed
  const SIMULATION_RANGES = {
    lifetime: { min: 1, max: 50},
    inflationRate: { min: -99, max: 999}, // percentage
    discountRate: { min: -99, max: 999}, // percentage
  };

  // onChange for inputs: capture raw input and show error if out of range; do NOT clamp
  const handleSimulationSettingChange = (key) => (event) => {
    const raw = event?.target?.value ?? '';
    setSimulationSettings((prev) => ({ ...prev, [key]: raw }));
    setHasUnsavedChanges(true);

    if (key in SIMULATION_RANGES) {
      const { min, max } = SIMULATION_RANGES[key];
      const num = Number(raw);
      let error = null;

      // Allow in-progress input, but show helpful error
      if (raw === '' || raw === '-' || raw === '+') {
        error = t("simulation.error.validNumber");
      } else if (Number.isNaN(num)) {
        error = t("simulation.error.validNumber");
      } else if (key === 'lifetime' && !/^-?\d+$/.test(raw)) {
        error = t("simulation.error.integerOnly");
      } else if (num < min || num > max) {
        error = t("simulation.error.valueBetween", { min, max });
      }

      setSimulationErrors((prev) => ({ ...prev, [key]: error }));
    }
  };

  const [kpiFactorsData, setKpiFactorsData] = useState([]);
  const [kpiValidity, setKpiValidity] = useState({}); // { [id]: boolean }
  const [initialSimulationType, setInitialSimulationType] = useState(null);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList)

  // const projectDesignVersion =
  //   projectDetails?.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0];
  // Polling interval ref (avoids state/closure issues across navigations)
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);
 
  // Function to call getsimulationcall
  const getsimulationcall = useCallback(() => {
    if (designId && designVersionId && projectId) {
      // const { designId, designVersionId, projectId } = projectDesignVersion;
      fetchSimulationData(projectId, designId, designVersionId);
    }
  }, [projectId, designId, designVersionId]);
  const callSnackbar = (type, message, duration = 8000) => {
    setSnackbarProps((prev) => ({
      ...prev,
      type,
      message,
      duration,
      open: true,
    }));

    const el = document.getElementById("snackbar-custom-1");
    if (el) {
      try {
        el.open = true;
        if (type === "error") el.canClose = true;
      } catch (e) {
        // ignore DOM errors in non-browser or custom element contexts
      }
    }

    setTimeout(() => {
      const el2 = document.getElementById("snackbar-custom-1");
      if (el2) {
        try {
          el2.open = false;
        } catch (e) {}
      }
      setSnackbarProps((prev) => ({ ...prev, open: false }));
    }, duration);
  };

  const fetchSensitivityScenarios = async () => {
    if (projectId && designId && designVersionId) {
      setLoading(true);
      // const { designId, designVersionId, projectId } = projectDesignVersion;
      MGA_API.getSensitivityScenarioData(projectId, designId, designVersionId)
        .then((res) => {
          if (res) {
            setInitialSensitivityData(res);
          }
        })
        .catch((error) => {
          callSnackbar("error", getErrorMessage(error), 8000);
          setInitialSensitivityData([]);
        });
      await handleMultipleSimulation(projectId, designId, designVersionId);
      setLoading(false);
    }
  };

  const KPI_DEFAULT_RANGES = {
    capitalExpenditure: {
      kpiName: "Upfront Capital",
      range: [0, 999999999],
      icon: projectDetails?.projectDTO?.currencyCode || "$",
    },
    // savings: { kpiName: "Savings", range: [-999, 999], icon: "%" },
    roi: { kpiName: "Return on Investment", range: [-999, 999999999], icon: "%" },
    // netPresentCost: {
    //   kpiName: "Net Present Cost",
    //   range: [0, 999999999],
    //   icon: projectDetails?.projectDTO?.currencyCode || "$",
    // },
    // lcoe: {
    //   kpiName: "Levelized Cost of Energy",
    //   range: [-99, 99],
    //   icon: projectDetails?.projectDTO?.currencyCode
    //     ? `${projectDetails.projectDTO.currencyCode}/kWh`
    //     : "$",
    // },
    // co2Emission: {
    //   kpiName: "CO2 Emission",
    //   range: [0, 99999999],
    //   icon: "Tons",
    // },
    payback:{
      kpiName: "Payback Time",
      range: [0, 99],
      icon: "Years",
    },
    irr: { kpiName: "Internal Rate of Return", range: [-999, 999999999], icon: <IconPercentage size={18} /> },
  };

  // Memoized and optimized handler to avoid unnecessary updates
  const handleKpiRangeChange = useCallback((id, val) => {
    setKpiFactorsData((prev) => {
      let changed = false;
      const next = prev.map((factor) => {
        if (factor?.id !== id) return factor;
        if (factor?.min === val[0] && factor?.max === val[1]) return factor;
        changed = true;
        return { ...factor, min: val[0], max: val[1] };
      });
      if (changed) setHasUnsavedChanges(true);
      return next;
    });
  }, []);

  const handleSensitivityTableChange = useCallback(() => {
    // Clear scenarios whenever sensitivity inputs change
    setInitialSensitivityData([]);
  }, []);

  const fetchKpiFactors = async (projectId, designId, designVersionId) => {
    setLoading(true);
    MGA_API.getkpiFactors(projectId, designId, designVersionId)
      .then((res) => {
        if (res?.kpiFactors?.length === 0) {
          setKpiFactorsData(defaultKpiFactors);
        } else {
          setKpiFactorsData(res?.kpiFactors);
        }
        const incoming = res?.projectSetting;
        const allNull = incoming && Object.values(incoming).every((v) => v === null || v === undefined);
        if (!incoming || allNull) {
          setSimulationSettings(INITIAL_SIMULATION_SETTINGS);
        } else {
          setSimulationSettings({
            startYear: (incoming?.startYear ?? INITIAL_SIMULATION_SETTINGS.startYear),
            lifetime: (incoming?.lifetime ?? INITIAL_SIMULATION_SETTINGS.lifetime),
            inflationRate: (incoming?.inflationRate ?? INITIAL_SIMULATION_SETTINGS.inflationRate),
            discountRate: (incoming?.discountRate ?? INITIAL_SIMULATION_SETTINGS.discountRate),
          });
        }

        setSimulationErrors({ lifetime: null, inflationRate: null, discountRate: null });
        setHasUnsavedChanges(false);
      })
      .catch((error) => {
        setKpiFactorsData([]);
        setSimulationSettings(INITIAL_SIMULATION_SETTINGS);
        setHasUnsavedChanges(false);
        callSnackbar("error", getErrorMessage(error));
      });
    setLoading(false);
  };

  // Refactored: Avoid duplicate API calls in checkSimulationStatus
  const fetchSimulationData = async (projectId, designId, designVersionId) => {
    try {
      setLoading(true);
      await MGA_API.simulationStatus(projectId, designId, designVersionId).then(
        (res) => {
          const resSimulationType = res?.simulationType || "SINGLE";
          setSimulationType(resSimulationType);
          setInitialSimulationType(resSimulationType);
          if (res?.message === "Simulation Not Available." || res?.status === "ACCEPTED") {
            setShowSimulation(false);
          } else {
            setShowSimulation(true);
          }

          if(res?.status === "COMPLETED"){
            setShowSimulationResult((prev) => ({
              ...res,
              status: "COMPLETED",
            }));
            MGA_API.editProject(projectId).then((res) => {
                setProjectDetails((prev) => ({
                  ...prev,  
                  userId: res.userId || "",
                  currencyCode: res?.projectDTO?.currencyCode || "$",
                  projectDTO: res.projectDTO || {},
                  componentDTOList:
                    flattenCountComponentDTOList(res.countComponentDTOList) ||
                    [],
                  designWithVersionsDTOList:
                    res.designWithVersionsDTOList || [],
                  locationDTO: res.locationDTO || {},
                  countComponentDTOList: res.countComponentDTOList,
                  designComponentsList:
                    flattenDesignCountComponent(res.designComponentsList) || [],
                }));
              })
              .catch((error)=>{
                const msg = getErrorMessage(error);
                callSnackbar("error", msg);
              })
          }else if (
            res?.status === "READY" ||
            res?.status === "PROCESSING" ||
            res?.status === "READING" || res?.status === "IN_QUEUE"
          ) {
            setShowSimulationResult((prev) => ({
              ...res,
              status: "PROCESSING",
            }));
          } else if (res?.status === "ERROR In AI HUB Status API" || res?.status.includes("ERROR")) {
            setShowSimulationResult((prev) => ({
              ...res,
              status: "ERROR",
            }));
            setShowSimulation(true);
          } else if (res?.status === "NOT STARTED") {
            setShowSimulationResult((prev) => ({
              ...res,
              status: "ERROR",
            }));
            setShowSimulation(false);
          }
        }
      );
      if (simulationType === "MULTIPLE") {
        await handleMultipleSimulation(projectId, designId, designVersionId);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);

      // handle error if needed
    }
  };

  useEffect(() => {
    const changedSimulationTypeCheck = _.isEqual(
      simulationType,
      initialSimulationType
    );

    if (!changedSimulationTypeCheck) {
      setShowSimulation(false);
    }
  }, [simulationType]);

  // Polling for simulation status when status is READING
  useEffect(() => {
    mountedRef.current = true;
    const shouldPoll =
    showSimuationResult?.status === "PROCESSING" || showSimuationResult?.status === "READY" || showSimuationResult?.status === "READING" || showSimuationResult?.status === "IN_QUEUE"  
    if (shouldPoll && !pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(() => {
        if (!mountedRef.current) return;
        if (document.visibilityState !== 'visible') return;
        getsimulationcall();
      }, 30000);
    }
    if (!shouldPoll && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [showSimuationResult?.status, getsimulationcall]);

  const handleMultipleSimulation = async (
    projectId,
    designId,
    designVersionId
  ) => {
    const results = await Promise.allSettled([
      MGA_API.getSensitivityParams(projectId, designId, designVersionId),
    ]);

    if (results[0]?.status === "fulfilled") {
      setInitialSimulationTable(results[0]?.value);
      const results1 = await Promise.allSettled([
        MGA_API.getProjectSensitivity(projectId, designId, designVersionId),
      ]);
      if (results1[0]?.status === "fulfilled") {
        setApiSimulationTable(results1[0]?.value);
      } else {
        // Show error message for failed project sensitivity call
        const reason = results1[0]?.reason;
        const msg = getErrorMessage(reason);
        callSnackbar('error', msg);
        setApiSimulationTable([]);
      }
    } else {
      // Show error message for failed sensitivity params call
      const reason = results[0]?.reason;
      const msg = getErrorMessage(reason);
      callSnackbar('error', msg);
      setInitialSimulationTable([]);
    }
  };

  const checkSimulationStatus = useCallback(() => {
    if (!projectId || !designId || !designVersionId) {
      MGA_API.editProject(projectId).then((res) => {
        setLoading(false);
        setProjectDetails((prev) => ({
          ...prev,
          userId: res.userId || "",
          currencyCode: res?.projectDTO?.currencyCode || "$",
          projectDTO: res.projectDTO || {},
          componentDTOList:
            flattenCountComponentDTOList(res.countComponentDTOList) || [],
          designWithVersionsDTOList: res.designWithVersionsDTOList || [],
          locationDTO: res.locationDTO || {},
          countComponentDTOList: res.countComponentDTOList,
          designComponentsList: flattenDesignCountComponent(res?.designComponentsList) || [],
        }));

        if (selectedDesignAndDesignVersion?.componentDTOList?.length > 0) {

          fetchSimulationData(
            projectId,
            designId,
            designVersionId
          );
          fetchKpiFactors(
            projectId,
            designId,
            designVersionId
          );
        }
      })
      .catch((error)=>{
        const msg = getErrorMessage(error);
        callSnackbar("error", msg);
      })
    } else {
      // const { designId, designVersionId, projectId } = projectDesignVersion;
      fetchSimulationData(projectId, designId, designVersionId);
      fetchKpiFactors(projectId, designId, designVersionId);
    }
  }, [
    MGA_API,
    projectId,
    designId,
    designVersionId,
    setApiSimulationTable,
    setInitialSimulationTable,
    setLoading,
    setProjectDetails,
    setShowSimulation,
    setShowSimulationResult,
    showSimulation,
  ]);

  useEffect(() => {
    checkSimulationStatus();
  }, []);

  useEffect(() => {
    if (simulationType === "MULTIPLE") {
      fetchSensitivityScenarios();
    }
  }, [simulationType, isSubmitClicked]);

  const [showCompletedVersionModal, setShowCompletedVersionModal] = useState(false);
  // const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const saveSimulationChanges = () => {
    if (hasUnsavedChanges) {
      callSnackbar(
        "error",
        t("simulation.error.saveSettingsFirst")
      );
      return;
    }
    if (simulationType === "MULTIPLE" && isScenarioSelectionValid === false) {
      callSnackbar("error", t("simulation.scenarios.selectAtLeastOne"));
      return;
    }
    if (
      simulationType === "MULTIPLE" &&
      (sensitivityDirty || !initialSensitivityData || initialSensitivityData.length === 0)
    ) {
      callSnackbar(
        "error",
        t("simulation.error.submitSensitivity")
      );
      return;
    }
    if (selectedVersion?.versionStatus === "COMPLETED") {
      setShowCompletedVersionModal(true);
      return;
    }
    setLoading(true);
    setRunLoading(true);
    const projectDesignVersion =
      // projectDetails?.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0];
    // const { designId, designVersionId, projectId } = projectDesignVersion;
    MGA_API.runSimulation(projectId, designId, designVersionId, simulationType)
      .then((res) => {
         MGA_API.editProject(projectId)
                      .then((res) => {
                        setProjectDetails((prev) => ({
                          ...prev,
                          userId: res.userId || "",
                          currencyCode: res?.projectDTO?.currencyCode || "$",
                          projectDTO: res.projectDTO || {},
                          componentDTOList:
                            flattenCountComponentDTOList(res.countComponentDTOList) ||
                            [],
                          designWithVersionsDTOList:
                            res.designWithVersionsDTOList || [],
                          locationDTO: res.locationDTO || {},
                          countComponentDTOList: res.countComponentDTOList,
                          designComponentsList:
                            flattenDesignCountComponent(res.designComponentsList) || [],
                        }));
                      })
        getsimulationcall();
        displayError("success", t("simulation.started"));
            setRunLoading(false);
      })
      .catch((error) => {
        setLoading(false);
            setRunLoading(false);
        setShowSimulationResult((prev) => ({
          ...prev,
          status: "ERROR",
          errorList: error?.response?.data?.message,
        }));
        setShowSimulation(true);
        callSnackbar("error", getErrorMessage(error), 8000);
        // displayError("error", t("simulation.run.error"));
      });
  };

  const handleErrorClick = () => setShowError(true);

  const cancelPopup = () => setShowError(false);

  const header = () => (
    <>
      <span className="stepText col-md-1 px-0">Step C.</span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("simulationStepA")}
      </span>
    </>
  );

  const children = () => (
    <Grid gutter="sm" className="row  mt-2 mb-2">
      {/* <LoadingOverlay visible={loading} /> */}
          <SimulationTable
            initialSimualtionTable={initialSimualtionTable}
            initialSensitivityData={initialSensitivityData}
            callSnackbar={callSnackbar}
            apiSimulationData={[apiSimulationTable]}
            setIsSubmitClicked={setIsSubmitClicked}
            setSensitivityDirty={setSensitivityDirty}
            onSensitivityChange={handleSensitivityTableChange}
            showSimuationResult={showSimuationResult}
            onCartesianChange={setCartesianSummary}
          />
          <span
            className="mb-3 mt-2"
            style={{ fontSize: "17px", color: "#eb7857" }}
          >
            <b>{t("common.note")}</b>
            <br />
            1. {t("simulation.note.sensitivityParams")} <br />
            {initialSensitivityData?.length
              ? <>{` 2. ${t("simulation.note.selectScenario")}`}<br /></>
              : null}
            {initialSensitivityData?.length ? "3" : "2"}. <strong>Sensitivity Factor Limit:</strong> Maximum allowed is <strong>100 combinations</strong>.
          </span>

          {initialSensitivityData?.length ? (
            <div className="col-sm-12 pr-5">
              <ScenariosTable
                initialSensitivityData={initialSensitivityData}
                callSnackbar={callSnackbar}
                setInitialSensitivityData={setInitialSensitivityData}
                showSimuationResult={showSimuationResult}
                setScenarioSelectionValid={setIsScenarioSelectionValid}
              />
            </div>
          ) : null}
    </Grid>
  );

  const header2 = () => (
    <>
      <span className="stepText col-md-1 px-0">Step A.</span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("simulationStepB")}
      </span>
    </>
  );

  const handleSaveProjectSettings = () => {
    const hasSimulationError = Object.values(simulationErrors || {}).some(
      (msg) => Boolean(msg)
    );
    const hasKpiError = Object.values(kpiValidity || {}).some((ok) => ok === false);
    if (hasSimulationError || hasKpiError) {
      callSnackbar(
        "error",
        t("simulation.error.fixKpiErrors"),
        8000
      );
      return;
    }

    setLoading(true);
    setSaveLoading(true);
    const payload = {
      projectId,
      designId,
      designVersionId,
      kpiFactors: kpiFactorsData,
      projectSetting: {
        startYear: Number(simulationSettings.startYear),
        lifetime: Number(simulationSettings.lifetime),
        inflationRate: Number(simulationSettings.inflationRate)?.toFixed(2),
        discountRate: Number(simulationSettings.discountRate)?.toFixed(2),
      },
    };

    MGA_API.saveKpiFactors(payload)
      .then((res) => {
        setKpiFactorsData(res?.kpiFactors || []);
        setLoading(false);
        setSaveLoading(false);
        setHasUnsavedChanges(false);
        callSnackbar("success", t("simulation.success.kpiSaved"));
      })
      .catch((error) => {
        setLoading(false);
        setSaveLoading(false);

        callSnackbar("error", getErrorMessage(error));
      });
  };

  const children2 = () => (
    <>
      <Grid gutter="md" className="mt-3 mb-3">
        <Text className="ms-2" size="md" weight={600}>
          {"Project Settings"}
        </Text>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={3}>
          <TextInput
            label={t("startYear")}
            required
            placeholder={t("simulation.placeholder.startYear")}
            type="text"
            value={simulationSettings?.startYear}
            onChange={handleSimulationSettingChange("startYear")}
            disabled
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            label={t("simulationLifeTime")}
            required
            type="text"
            id="lifetime"
            name="lifetime"
            placeholder={t("simulation.placeholder.lifetime")}
            value={simulationSettings?.lifetime}
            onChange={handleSimulationSettingChange("lifetime")}
            error={simulationErrors?.lifetime}
            rightSection={
              <div style={{ padding: "4px", marginRight: "1rem" }}>
                {t("common.years")}
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            label={t("simulationInflation")}
            required
            type="text"
            value={simulationSettings?.inflationRate}
            onChange={handleSimulationSettingChange("inflationRate")}
            error={simulationErrors?.inflationRate}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            label={t("simulationDiscount")}
            required
            type="text"
            value={simulationSettings?.discountRate}
            onChange={handleSimulationSettingChange("discountRate")}
            error={simulationErrors?.discountRate}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-3 mb-3">
        <Text className="ms-2" size="md" weight={600}>
          {t("simulation.kpi.header")}
        </Text>
      </Grid>
      <Grid gutter="md" className="mt-2">
        {(!kpiFactorsData || kpiFactorsData?.length === 0) ? (
          <Grid.Col span={12}>
            <SelectedKpiSkeleton count={4} />
          </Grid.Col>
        ) : (
          kpiFactorsData?.map((item, index) => {
            const kpiDefaultRange = KPI_DEFAULT_RANGES?.[item?.name]?.range;
            return (
              <Grid.Col span={3} key={item?.id}>
                <span style={{ marginBottom: 8,marginLeft:35 }}>
                  {KPI_DEFAULT_RANGES?.[item?.name]?.kpiName}
                </span>
                <KPILimitsRange
                  key={index}
                  introKey={index}
                  name={item?.name}
                  initialMin={kpiDefaultRange?.[0]}
                  initialMax={kpiDefaultRange?.[1]}
                  initialValue={[item?.min, item?.max]}
                  rightIcon={KPI_DEFAULT_RANGES?.[item?.name]?.icon}
                  onChange={(val) => handleKpiRangeChange(item?.id, val)}
                  onValidityChange={(isValid) =>
                    setKpiValidity((prev) => {
                      const current = prev?.[item?.id];
                      if (current === isValid) return prev;
                      return { ...prev, [item?.id]: isValid };
                    })
                  }
                />
              </Grid.Col>
            );
          })
        )}
               
      </Grid>
      <Grid  gutter="md" className="mt-2 mb-2 ml-2">
        <Grid.Col>
           <Button
          loading={saveLoading || !kpiFactorsData?.length}
          color="seGreen.0"
          onClick={handleSaveProjectSettings}
          style={{
            color: colorScheme === "dark" ? "#2e3032" : "#ffffff",
          }}
          data-intro={t("tour.saveComponentConfiguration")}
          data-position="left"
          disabled={!userScope?.access?.saveUpdateComponent}
        >
          <SeIcon aria-hidden="true">action_save</SeIcon>
          &nbsp;{t("simulation.button.submit")}
        </Button>
        </Grid.Col>
        </Grid>
    </>
  );

  const header3 = () => (
    <>
      <span className="stepText col-md-1 px-0">Step B.</span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("simulationStepC")}
      </span>
    </>
  );

  const SimulationData = () => (
    <div className="col-sm-8">
      <div
        style={{
          paddingRight: "20px",
          paddingLeft: "20px",
          border: "1px solid var(--se-accordian-standard)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px",
            borderBottom: "1px solid var(--se-accordian-standard)",
          }}
        >
          {t("simulation.status.header")}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: "10px",
          }}
        >
          <div>
            <div className="heading-text">
              {t("simulation.status.type")}:&nbsp;
              {showSimuationResult &&
              showSimuationResult?.simulationType === "MULTIPLE"
                ? t("simulation.type.sensitivityAnalysis")
                : t("simulation.type.designSimulation")}
            </div>
            <div className="sub-text">{`${t("simulation.status.executionStarted")}: ${
              showSimuationResult && showSimuationResult?.startDateTime
                ? (formatToUTCWithGMT(showSimuationResult?.startDateTime) || t("simulation.status.notAvailable"))
                : t("simulation.status.notAvailable")
            }`}</div>
            <div className="sub-text">{`${t("simulation.status.executionEnded")}: ${
              showSimuationResult && showSimuationResult?.endDateTime
                ? (formatToUTCWithGMT(showSimuationResult?.endDateTime) || t("simulation.status.notAvailable"))
                : t("simulation.status.notAvailable")
            }`}</div>

            <div className="sub-text">{`${t("simulation.status.totalTime")}: ${
              showSimuationResult && showSimuationResult.status === "COMPLETED"
                ? `${Math.floor(showSimuationResult?.hours)}H ${Math.floor(
                    showSimuationResult?.minutes
                  )}M ${Math.floor(showSimuationResult?.seconds)}S`
                : t("simulation.status.notAvailable")
            }`}</div>
          </div>
          <div>
            <div>
              <OrderTrackingVertical
                currentStatus={
                  showSimuationResult?.status === "ERROR"
                    ? "In Progress (error)"
                    : showSimuationResult?.status === "COMPLETED"
                    ? "Completed"
                    : "In Progress"
                }
              />
            </div>
          </div>{" "}
          <div>
            <div className="heading-text">
              {t("simulation.status.dataValidation")}:{" "}
              <span
                style={{
                  color:
                    showSimuationResult?.status === "ERROR"
                      ? "red"
                      : showSimuationResult?.status === "COMPLETED"
                      ? "#3dcd58"
                      : "#42b4e6"
                }}
              >
                {`${
                  showSimuationResult?.status === "ERROR"
                    ? t("simulation.status.error")
                    : showSimuationResult?.status === "COMPLETED"
                    ? t("simulation.status.completed")
                    : t("simulation.status.inProgress")
                }`}{" "}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <p>
                {showSimuationResult?.status === "ERROR"
                  ? ""
                  : showSimuationResult?.status === "PROCESSING"
                    ? t("simulation.status.checkingErrors")
                    : t("simulation.status.noErrors")}
              </p>
              <p>
                {showSimuationResult?.status === "PROCESSING" ? (
                  <se-icon size="large" aria-label="simulation-status-processing">action_search_project</se-icon>
                ) : showSimuationResult?.status === "COMPLETED" ? (
                  <se-icon size="large" aria-label="simulation-status-completed">action_test</se-icon>
                ) : ((consolidatedErrors?.length > 0 || (showSimuationResult?.errorList && showSimuationResult?.errorList?.length > 0) || showSimuationResult?.errors?.length > 0)) ? (
                  <>
                    &nbsp;
                    <Button
                      color="gray"
                      onClick={handleErrorClick}
                      variant="filled"
                    >
                      {t("simulation.button.viewErrors")}
                    </Button>
                  </>
                ): null}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const children3 = () => {
    const isSimulationSelectionLocked =
      showSimuationResult?.status === "COMPLETED" ||
      showSimuationResult?.status === "PROCESSING";

    const handleSimulationTypeSelect = (type) => {
      if (isSimulationSelectionLocked) return;
      setSimulationType(type);
    };

    const handleSimulationTypeKeyDown = (event, type) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSimulationTypeSelect(type);
      }
    };

    return (
      <div className="col-sm-12 ms-1 mt-3 mb-3">
        <div className="row">
          <div className="col-sm-12">
            {/* <div className="row label ms-1 mb-2">
              {t("simulation.selectType")}
            </div> */}
            <div style={{ display: "flex", justifyContent: "center", gap: "5%" }}>
              <div
                className={`simulation-type-card ${
                  simulationType === "SINGLE" ? "selected" : ""
                } ${colorScheme === "dark" ? "bg-dark" : ""}`}
                role="button"
                tabIndex={isSimulationSelectionLocked ? -1 : 0}
                aria-disabled={isSimulationSelectionLocked}
                onClick={() => handleSimulationTypeSelect("SINGLE")}
                onKeyDown={(e) => handleSimulationTypeKeyDown(e, "SINGLE")}
                style={{
                  width: "40%",
                  border:
                    simulationType === "SINGLE"
                      ? "2px solid #3dcd58"
                      : "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "16px",
                  cursor: "pointer",
                  marginBottom: "16px",
                  background: colorScheme === "dark" ? "#23272b" : "#fff",
                  textAlign: "center",
                  // pointerEvents: isSimulationSelectionLocked ? "none" : "auto",
                  // opacity: isSimulationSelectionLocked ? 0.8 : 1,
                }}
                data-intro={t("tour.designSimulation")}
                data-position="bottom"
              >
                <div className="loadSubHeading mt-2 mb-2">{t("simulation.type.designSimulation")}</div>
                <div className="loadText mb-2" style={{ fontSize: "0.8rem !important" }}>{t("singleSimulation")}</div>
              </div>
              <div
                className={`simulation-type-card ${
                  simulationType === "MULTIPLE" ? "selected" : ""
                } ${colorScheme === "dark" ? "bg-dark" : ""}`}
                role="button"
                tabIndex={isSimulationSelectionLocked ? -1 : 0}
                aria-disabled={isSimulationSelectionLocked}
                onClick={() => handleSimulationTypeSelect("MULTIPLE")}
                onKeyDown={(e) => handleSimulationTypeKeyDown(e, "MULTIPLE")}
                style={{
                  width: "40%",
                  border:
                    simulationType === "MULTIPLE"
                      ? "2px solid #3dcd58"
                      : "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "16px",
                  cursor: "pointer",
                  marginBottom: "16px",
                  background: colorScheme === "dark" ? "#23272b" : "#fff",
                  textAlign: "center",
                  pointerEvents: isSimulationSelectionLocked ? "none" : "auto",
                  opacity: isSimulationSelectionLocked ? 0.6 : 1,
                }}
                data-intro={t("tour.sensitivityAnalysis")}
                data-position="bottom"
              >
                <div className="loadSubHeading mt-2 mb-2">
                  {t("simulation.type.sensitivityAnalysis")}
                </div>
                <div className="loadText mb-2 ">{t("fullSimulation")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const { type, message } = snackbarProps;

  // Consolidated errors list (supports newline-delimited string or errors array)
  const consolidatedErrors = (() => {
    const list = [];
    if (showSimuationResult?.errorList) {
      try {
        const splitErrors = showSimuationResult?.errorList
          .split('\n')
          .map(e => e.trim())
          .filter(e => e?.length > 0);
        list.push(...splitErrors);
      } catch (e) {
        if (typeof showSimuationResult?.errorList === 'string') {
          list.push(showSimuationResult?.errorList);
        }
      }
    }
    if (Array.isArray(showSimuationResult?.errors) && showSimuationResult?.errors?.length > 0) {
      // Append distinct errors from errors array
      showSimuationResult?.errors?.forEach(err => {
        if (err && !list.includes(err)) list.push(err);
      });
    }

    if (showSimuationResult?.errorTitle) {
      list.push(showSimuationResult?.errorTitle);
    }

    return list;
  })();

  return (
    <>
          
      <se-loading loading={loading} />
      <div className="col-sm-12 px-0">
        <div
          style={{
            position: "sticky",
            top: "0px",
            zIndex: "10",
            minHeight: "60px",
            backgroundColor:
              colorScheme === "dark"
                ? "var(--se-background-alternative)"
                : "#fff",
          }}
        >
          <ManageProgressBar />
        </div>
        <div className="col-sm-12" style={{ paddingInline: "1%" }}>
    <se-snackbar
            id="snackbar-custom-1"
            type={type}
            icon="information_stroke"
            message={message}
            duration={snackbarProps.duration}
          ></se-snackbar>

          <AccordionItem
            id={1}
            height="fit-content"
            header={header2()}
            key={1}
            children={children2()}
            top={true}
            open={selectedVersion?.versionStatus === "COMPLETED" || showSimuationResult?.status === "PROCESSING" ? false : true}
            childrenStyle={{
              pointerEvents: (selectedVersion?.versionStatus === "COMPLETED" || showSimuationResult?.status === "PROCESSING") ? "none" : "auto",
              opacity: selectedVersion?.versionStatus === "COMPLETED" ? 0.8 : 1,
            }}
          />
          <AccordionItem
            height="fit-content"
            id={2}
            header={header3()}
            key={2}
            children={children3()}
            top={true}
            open={selectedVersion?.versionStatus === "COMPLETED"  || showSimuationResult?.status === "PROCESSING"? false : true}
            childrenStyle={{
              pointerEvents: (selectedVersion?.versionStatus === "COMPLETED" || showSimuationResult?.status === "PROCESSING") ? "none" : "auto",
              opacity: selectedVersion?.versionStatus === "COMPLETED" ? 0.8 : 1,
            }}
          />
          {simulationType === "MULTIPLE" && (
            <AccordionItem
              id={3}
              header={header()}
              key={3}
              children={children()}
              height="fit-content"
              top={true}
              open={selectedVersion?.versionStatus === "COMPLETED" || showSimuationResult?.status === "PROCESSING" ? false : true}
              // childrenStyle={{
              //   pointerEvents: (selectedVersion?.versionStatus === "COMPLETED" || showSimuationResult?.status === "PROCESSING") ? "none" : "auto",
              //   opacity: selectedVersion?.versionStatus === "COMPLETED" ? 0.8 : 1,
              // }}
            />
          )}
        </div>
        <div className="col-sm-12 d-flex justify-content-center mt-4 mb-3">
          <Button
            option="flat"
            color="seGreen.0"
            onClick={saveSimulationChanges}
            disabled={
              showSimuationResult?.status === "PROCESSING" ||
              !userScope?.access?.triggerSimulation
            }
            loading={runLoading}
            style={{
            color: colorScheme === "dark" ? "#2e3032" : "#ffffff",
          }}
            data-intro={t("tour.runSimulation")}
            data-position="top"
          >
            {t("simulation.button.runSimulation")}
          </Button>
        </div>
        {showSimulation && <div
          className="row"
          style={{ justifyContent: "center", marginTop: "30px",marginBottom:"30px" }}
          data-intro={t("tour.simulationStatusResults")}
          data-position="top"
        >
          {showSimulation ? <SimulationData /> : null}
        </div>}

        {progressListAtom?.status && <CircularTimer />}
        <se-dialog
          id="dialog-complex"
          size="xlarge"
          open={showError}
          can-backdrop="true"
        >
                  <se-dialog-header>{t("simulation.dialog.errorList")}</se-dialog-header>
          <se-dialog-content>
            <ul>
              {consolidatedErrors?.length > 0 ? (
                consolidatedErrors?.map((error, index) => (
                  <li key={index} style={{ marginBottom: '10px' }}>
                    • {error}
                  </li>
                ))
              ) : (
                                <li style={{ marginBottom: '10px' }}>• {t("simulation.dialog.noErrors")}</li>
              )}
            </ul>
          </se-dialog-content>
          <se-dialog-footer>
            <se-button aria-label="simulation-error-dialog-close" onClick={cancelPopup}>{t("common.cancel")}</se-button>
          </se-dialog-footer>
        </se-dialog>
        {/* Modal for completed version */}
        <se-dialog
          id="dialog-completed-version"
          size="large"
          open={showCompletedVersionModal}
          can-backdrop="true"
        >
                    <se-dialog-header>{t("simulation.dialog.notAllowed")}</se-dialog-header>
          <se-dialog-content>
            <div style={{marginBottom: "20px"}}>
              {t("simulation.dialog.versionCompleted", { version: selectedVersion?.version || "(unknown)" })}<br />
              {t("simulation.dialog.chooseNewVersion")}
            </div>
          </se-dialog-content>
          <se-dialog-footer>
            <se-button aria-label="simulation-completed-dialog-ok" onClick={() => setShowCompletedVersionModal(false)}>{t("common.ok")}</se-button>
          </se-dialog-footer>
        </se-dialog>
      </div>
    </>
  );
};

export default Simulation;
